import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast} from "react-toastify";
import "../css/style.css";
import logo from "../assets/image/rccglogo.webp";
import { Eye, EyeOff } from "lucide-react";

export default function NewPassword() {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const navigate = useNavigate();
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    const verifiedEmail = localStorage.getItem("verifiedEmail")?.trim();
    const verifiedOtp = localStorage.getItem("verifiedOtp")?.trim();

    console.log("Retrieved Email:", verifiedEmail);
    console.log("Retrieved OTP:", verifiedOtp);
    console.log("OTP Length:", verifiedOtp?.length);

    if (!verifiedEmail || !verifiedOtp) {
      toast.error("Please verify your OTP first.", { theme: "colored" });
      navigate("/forgot-password", { replace: true });
    } else if (verifiedOtp.length !== 6 || !/^\d+$/.test(verifiedOtp)) {
      toast.error("Invalid OTP format stored. Please start over.", { theme: "colored" });
      localStorage.removeItem("verifiedEmail");
      localStorage.removeItem("verifiedOtp");
      navigate("/forgot-password", { replace: true });
    } else {
      setEmail(verifiedEmail);
    }

    return () => {
      isMounted.current = false;
    };
  }, [navigate]);

  const handleResetPassword = async (e) => {
    e.preventDefault();

    // Basic validations
    if (!password.trim() || !confirmPassword.trim()) {
      toast.error("Please fill in both password fields.", { theme: "colored" });
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters long.", {
        theme: "colored",
      });
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Passwords do not match.", { theme: "colored" });
      return;
    }

    const verifiedEmail = localStorage.getItem("verifiedEmail")?.trim();
    const otp = localStorage.getItem("verifiedOtp")?.trim();

    if (!verifiedEmail || !otp) {
      toast.error("Please verify your OTP first.", { theme: "colored" });
      navigate("/forgot-password", { replace: true });
      return;
    }

    setLoading(true);

    try {
      const TEST_BASE_URL = process.env.REACT_APP_BASE_URL;

      // Prepare payload exactly as backend expects
      const payload = {
        email: verifiedEmail,
        password,
        password_confirmation: confirmPassword,
        otp: otp,
      };

      console.log("Final Payload to send:", payload);
      console.log("Payload JSON:", JSON.stringify(payload));

      const res = await fetch(`${TEST_BASE_URL}/auth/reset-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const text = await res.text();
      console.log("Response Status:", res.status);
      console.log("Response Text:", text);

      let data = {};
      try {
        data = JSON.parse(text);
      } catch {
        console.warn("Response is not JSON:", text);
      }

      if (!res.ok) {
        const errorMessages = data.errors
          ? Object.values(data.errors).flat().join(" ")
          : data.message || "Password reset failed.";
        
        console.error("Error Details:", data);
        toast.error(errorMessages, { theme: "colored" });
        setLoading(false);
        return;
      }

      toast.success("Password reset successfully!", { theme: "colored" });

      // Clean localStorage
      localStorage.removeItem("verifiedEmail");
      localStorage.removeItem("verifiedOtp");
      localStorage.removeItem("resetEmail");

      setTimeout(() => {
        navigate("/login", { replace: true });
      }, 1500);
    } catch (err) {
      console.error("Catch Error:", err);
      toast.error("Network error — please try again.", { theme: "colored" });
      setLoading(false);
    }
  };

  return (
    <>
      <section className="login">
        <div className="containerfluid">
          <div className="row loginrow">
            <div className="col-md-6">
              <div className="loginbg">
                <img src={logo} alt="RCCG Logo" />
                <h5 className="py-5">
                  Welcome to RCCG New Beginning Chapel, Bournemouth, UK
                </h5>
                <p className="text-white">
                  RCCG Bournemouth Membership Portal has been built to create a
                  community where the church members can interact with each
                  other seamlessly.
                </p>
              </div>
            </div>

            <div className="col-md-6">
              <form className="card" onSubmit={handleResetPassword}>
                <h5>Create New Password</h5>
                <p className="text-muted">Resetting password for: {email}</p>

                <label htmlFor="password">Password</label>
                <div
                  className="password-input-wrapper"
                  style={{ position: "relative" }}
                >
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    className="form-control mb-3"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="eye-icon"
                  >
                    {showPassword ? (
                      <EyeOff size={20} color="#666" />
                    ) : (
                      <Eye size={20} color="#666" />
                    )}
                  </button>
                </div>

                <label htmlFor="confirmPassword">Confirm Password</label>
                <div
                  className="password-input-wrapper"
                  style={{ position: "relative" }}
                >
                  <input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Confirm your password"
                    className="form-control"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="eye-icon"
                  >
                    {showConfirmPassword ? (
                      <EyeOff size={20} color="#666" />
                    ) : (
                      <Eye size={20} color="#666" />
                    )}
                  </button>
                </div>

                <button
                  type="submit"
                  className="mt-5 btn w-100 resetpass"
                  disabled={loading}
                >
                  {loading ? "Resetting..." : "Reset Password"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}